import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { 
  ArrowLeft, Image, Film, Trash2, Star, 
  ChevronDown, Loader2, GripVertical, CheckSquare, Square, Move, X, FolderOpen
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const FLEET_SLUGS = [
  { slug: 'premium-escalade', name: 'Cadillac Escalade / Escalade ESV / Yukon XL / Suburban' },
  { slug: 'luxury-sedan', name: 'Mercedes S450 / BMW 760Li' },
  { slug: 'sprinter-shuttle', name: 'Sprinter Shuttle w/ Table' },
  { slug: 'sprinter-luxury', name: 'Luxury Sprinter Shuttle' },
  { slug: 'sprinter-limo', name: 'Sprinter Limo Style' },
  { slug: 'sprinter-executive', name: 'Executive Sprinter Van' },
  { slug: 'sprinter-tour', name: 'Sprinter Tour Van' },
  { slug: 'mini-bus', name: 'Executive Mini Bus' },
  { slug: 'executive-bus', name: 'Executive Bus' },
  { slug: 'bentley-flying-spur', name: 'Bentley Flying Spur' },
  { slug: 'bentley-bentayga', name: 'Bentley Bentayga SUV' },
  { slug: 'lamborghini-urus', name: 'Lamborghini Urus' },
  { slug: 'maybach-s600', name: 'Maybach S600 Sedan' },
  { slug: 'maybach-gls600', name: 'Maybach GLS600 SUV' },
  { slug: 'maybach-s580', name: 'Maybach S580 (2023)' },
  { slug: 'rolls-royce-ghost', name: 'Rolls-Royce Ghost' },
  { slug: 'rolls-royce-cullinan', name: 'Rolls-Royce Cullinan SUV' },
  { slug: 'escalade-esv', name: 'Escalade ESV / Yukon XL / Suburban' },
  { slug: 'cadillac-escalade-esv-concierge', name: '2023 Cadillac Escalade ESV' },
  { slug: 'wedding-getaway', name: 'Wedding Getaway Fleet' },
  { slug: 'exotic-collection', name: 'Exotic Collection' },
];

const AdminFleetMedia = () => {
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [vehicleMedia, setVehicleMedia] = useState({ images: [], videos: [] });
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [loading, setLoading] = useState(true);
  const [featuredVideo, setFeaturedVideo] = useState(null);
  const [allMedia, setAllMedia] = useState([]);
  // Bulk selection state
  const [selected, setSelected] = useState(new Set());
  const [bulkMode, setBulkMode] = useState(false);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/auth/me`, { credentials: 'include' })
      .then(r => { if (!r.ok) navigate('/admin/login'); })
      .catch(() => navigate('/admin/login'));
  }, [navigate]);

  const loadVehicles = useCallback(async () => {
    try {
      const [pricingRes, mediaRes] = await Promise.all([
        fetch(`${API}/api/pricing/vehicles`),
        fetch(`${API}/api/fleet-media/all`)
      ]);
      const pricingData = await pricingRes.json();
      const mediaData = await mediaRes.json();
      const pricingVehicles = pricingData.vehicles || [];
      const mediaVehicles = (mediaData.vehicles || []).map(m => ({
        vehicle_id: m.vehicle_id,
        vehicle_name: m.vehicle_name || m.vehicle_id,
        category: m.category || 'uncategorized',
      }));
      const pricingIds = new Set(pricingVehicles.map(v => v.vehicle_id));
      setVehicles([...pricingVehicles, ...mediaVehicles.filter(m => !pricingIds.has(m.vehicle_id))]);
    } catch { }
    setLoading(false);
  }, []);

  const loadAllMedia = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/fleet-media/all`);
      const data = await res.json();
      setAllMedia(data.vehicles || []);
    } catch { }
  }, []);

  const loadFeaturedVideo = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/fleet-media/site-featured`);
      const data = await res.json();
      setFeaturedVideo(data.url);
    } catch { }
  }, []);

  useEffect(() => { loadVehicles(); loadAllMedia(); loadFeaturedVideo(); }, [loadVehicles, loadAllMedia, loadFeaturedVideo]);

  const loadVehicleMedia = async (vehicleId) => {
    try {
      const res = await fetch(`${API}/api/fleet-media/${vehicleId}`);
      const data = await res.json();
      setVehicleMedia({ images: data.images || [], videos: data.videos || [], vehicle_slug: data.vehicle_slug || '' });
    } catch {
      setVehicleMedia({ images: [], videos: [], vehicle_slug: '' });
    }
  };

  const selectVehicle = (v) => {
    setSelectedVehicle(v);
    loadVehicleMedia(v.vehicle_id);
    setSelected(new Set());
    setBulkMode(false);
  };

  const uploadFiles = async (files) => {
    if (!files.length || !selectedVehicle) return;
    const media = files.filter(f => f.type.startsWith('image/') || f.type.startsWith('video/'));
    if (!media.length) return;
    setUploading(true);
    let done = 0;
    const uploadOne = async (file) => {
      const type = file.type.startsWith('video/') ? 'video' : 'image';
      const formData = new FormData();
      formData.append('file', file);
      try {
        await fetch(`${API}/api/fleet-media/${selectedVehicle.vehicle_id}/${type}`, { method: 'POST', body: formData });
      } catch (err) { console.error('Upload failed:', err); }
      done += 1;
      setUploadProgress(`Uploading ${done}/${media.length}…`);
    };
    // Parallel batches of 3 — order preserved by upload sequence within batches
    for (let i = 0; i < media.length; i += 3) {
      await Promise.all(media.slice(i, i + 3).map(uploadOne));
    }
    setUploading(false);
    setUploadProgress('');
    loadVehicleMedia(selectedVehicle.vehicle_id);
    loadAllMedia();
  };

  const handleUpload = async (type) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = type === 'image' ? 'image/*' : 'video/*';
    input.multiple = true;
    input.onchange = (e) => uploadFiles(Array.from(e.target.files));
    input.click();
  };

  const [dragActive, setDragActive] = useState(false);
  const dragDepth = useRef(0);
  const dropHandlers = selectedVehicle ? {
    onDragEnter: (e) => {
      if (![...e.dataTransfer.types].includes('Files')) return;
      e.preventDefault();
      dragDepth.current += 1;
      setDragActive(true);
    },
    onDragOver: (e) => { if ([...e.dataTransfer.types].includes('Files')) e.preventDefault(); },
    onDragLeave: (e) => {
      if (![...e.dataTransfer.types].includes('Files')) return;
      dragDepth.current -= 1;
      if (dragDepth.current <= 0) { dragDepth.current = 0; setDragActive(false); }
    },
    onDrop: (e) => {
      e.preventDefault();
      dragDepth.current = 0;
      setDragActive(false);
      uploadFiles(Array.from(e.dataTransfer.files));
    },
  } : {};

  const handleDelete = async (type, fileId) => {
    if (!selectedVehicle) return;
    const r = await fetch(`${API}/api/fleet-media/${selectedVehicle.vehicle_id}/${type}/${fileId}`, { method: 'DELETE', credentials: 'include' });
    if (!r.ok) { alert('Delete failed — please try again'); return; }
    loadVehicleMedia(selectedVehicle.vehicle_id);
    loadAllMedia();
  };

  // Bulk operations
  const toggleSelect = (fileId) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(fileId) ? next.delete(fileId) : next.add(fileId);
      return next;
    });
  };

  const selectAll = () => {
    const allIds = [...vehicleMedia.images.map(i => i.file_id), ...vehicleMedia.videos.map(v => v.file_id)];
    setSelected(new Set(allIds));
  };

  const deselectAll = () => setSelected(new Set());

  const handleBulkDelete = async () => {
    if (!selectedVehicle || selected.size === 0) return;
    if (!window.confirm(`Delete ${selected.size} item(s)? This cannot be undone.`)) return;
    setBulkLoading(true);
    const r = await fetch(`${API}/api/fleet-media/bulk-delete`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ vehicle_id: selectedVehicle.vehicle_id, file_ids: [...selected] })
    });
    if (!r.ok) { alert('Bulk delete failed — please try again'); setBulkLoading(false); return; }
    setSelected(new Set());
    setBulkMode(false);
    setBulkLoading(false);
    loadVehicleMedia(selectedVehicle.vehicle_id);
    loadAllMedia();
  };

  const handleBulkMove = async (targetVehicleId) => {
    if (!selectedVehicle || selected.size === 0) return;
    setBulkLoading(true);
    await fetch(`${API}/api/fleet-media/bulk-move`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        source_vehicle_id: selectedVehicle.vehicle_id,
        target_vehicle_id: targetVehicleId,
        file_ids: [...selected]
      })
    });
    setSelected(new Set());
    setBulkMode(false);
    setShowMoveModal(false);
    setBulkLoading(false);
    loadVehicleMedia(selectedVehicle.vehicle_id);
    loadAllMedia();
    loadVehicles();
  };

  // Drag reorder
  const dragItem = useRef(null);
  const dragOverItem = useRef(null);
  const handleDragStart = (idx) => { dragItem.current = idx; };
  const handleDragEnter = (idx) => { dragOverItem.current = idx; };
  const handleDragEnd = async () => {
    if (dragItem.current === null || dragOverItem.current === null || !selectedVehicle) return;
    const items = [...vehicleMedia.images];
    const dragged = items.splice(dragItem.current, 1)[0];
    items.splice(dragOverItem.current, 0, dragged);
    setVehicleMedia(prev => ({ ...prev, images: items }));
    dragItem.current = null;
    dragOverItem.current = null;
    await fetch(`${API}/api/fleet-media/${selectedVehicle.vehicle_id}/reorder`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ type: 'images', order: items.map(i => i.file_id) })
    });
    loadAllMedia();
  };

  const setAsFeatured = async (url) => {
    if (!selectedVehicle) return;
    await fetch(`${API}/api/fleet-media/${selectedVehicle.vehicle_id}/featured`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ featured_image: url })
    });
    loadVehicleMedia(selectedVehicle.vehicle_id);
  };

  const setSiteFeaturedVideo = async (url) => {
    await fetch(`${API}/api/fleet-media/site-featured`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ url })
    });
    setFeaturedVideo(url);
  };

  const mediaCount = (vehicleId) => {
    const m = allMedia.find(a => a.vehicle_id === vehicleId);
    return m ? (m.images?.length || 0) + (m.videos?.length || 0) : 0;
  };

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-red-600" />
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-fleet-media">
      {/* Header */}
      <div className="bg-black text-white px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/admin')} className="text-gray-400 hover:text-white" data-testid="back-to-admin">
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold">Fleet Media Manager</h1>
              <p className="text-sm text-gray-400">Upload, manage, move, and delete fleet photos & videos</p>
            </div>
          </div>
          <button onClick={() => navigate('/admin/media-library')} className="inline-flex items-center gap-1.5 text-xs font-bold bg-white/10 hover:bg-white/20 border border-white/20 text-white px-3.5 py-2 rounded-lg" data-testid="open-media-library">
            <FolderOpen className="h-3.5 w-3.5" /> Media Library
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Vehicle List */}
          <div className="space-y-4">
            <h2 className="text-lg font-bold text-black">Select Vehicle</h2>
            <div className="space-y-2 max-h-[70vh] overflow-y-auto pr-2">
              {vehicles.length === 0 && (
                <Card className="p-6 text-center text-gray-500">
                  <p className="mb-2">No vehicles found.</p>
                  <Button onClick={() => navigate('/pricing-hq')} variant="outline" size="sm">Go to Pricing HQ</Button>
                </Card>
              )}
              {vehicles.map((v) => (
                <Card
                  key={v.vehicle_id}
                  className={`p-4 cursor-pointer transition-all hover:shadow-md ${
                    selectedVehicle?.vehicle_id === v.vehicle_id ? 'border-red-600 border-2 bg-red-50' : 'border-gray-200'
                  }`}
                  onClick={() => selectVehicle(v)}
                  data-testid={`select-vehicle-${v.vehicle_id}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-sm text-black">{v.vehicle_name}</p>
                      <p className="text-xs text-gray-500 capitalize">{v.category?.replace('_', ' ')}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {mediaCount(v.vehicle_id) > 0 && (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                          {mediaCount(v.vehicle_id)} files
                        </span>
                      )}
                      <ChevronDown className="h-4 w-4 text-gray-400" />
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>

          {/* Media Manager */}
          <div className="lg:col-span-2">
            {!selectedVehicle ? (
              <Card className="p-12 text-center border-gray-200">
                <Image className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-400 mb-2">Select a Vehicle</h3>
                <p className="text-sm text-gray-400">Choose a vehicle from the list to manage its photos and videos</p>
              </Card>
            ) : (
              <div className="space-y-6 relative" {...dropHandlers} data-testid="media-dropzone">
                {dragActive && (
                  <div className="absolute inset-0 z-30 bg-blue-600/10 border-4 border-dashed border-blue-500 rounded-xl flex items-center justify-center pointer-events-none" data-testid="dropzone-overlay">
                    <div className="bg-white shadow-xl rounded-xl px-8 py-6 text-center">
                      <Image className="h-10 w-10 text-blue-600 mx-auto mb-2" />
                      <p className="text-lg font-bold text-blue-700">Drop photos or videos here</p>
                      <p className="text-xs text-gray-500 mt-1">They'll upload to {selectedVehicle.vehicle_name} in order</p>
                    </div>
                  </div>
                )}
                {/* Vehicle Header + Upload + Bulk Toggle */}
                <Card className="p-6 border-gray-200">
                  <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                    <div>
                      <h3 className="text-xl font-bold text-black">{selectedVehicle.vehicle_name}</h3>
                      <p className="text-sm text-gray-500">ID: {selectedVehicle.vehicle_id} | {selectedVehicle.category?.replace('_', ' ')}</p>
                      <p className="text-[11px] text-blue-500 mt-1">💡 Tip: drag &amp; drop photos from your computer anywhere on this panel to bulk-upload</p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => { setBulkMode(!bulkMode); setSelected(new Set()); }}
                        variant={bulkMode ? 'default' : 'outline'}
                        className={bulkMode ? 'bg-indigo-600 hover:bg-indigo-700' : ''}
                        data-testid="bulk-mode-btn"
                      >
                        <CheckSquare className="h-4 w-4 mr-2" />
                        {bulkMode ? 'Cancel Select' : 'Select'}
                      </Button>
                      <Button onClick={() => handleUpload('image')} disabled={uploading} className="bg-blue-600 hover:bg-blue-700" data-testid="upload-image-btn">
                        <Image className="h-4 w-4 mr-2" /> Upload Photos
                      </Button>
                      <Button onClick={() => handleUpload('video')} disabled={uploading} className="bg-purple-600 hover:bg-purple-700" data-testid="upload-video-btn">
                        <Film className="h-4 w-4 mr-2" /> Upload Video
                      </Button>
                    </div>
                  </div>

                  {/* Bulk Action Bar */}
                  {bulkMode && (
                    <div className="flex flex-wrap items-center gap-3 p-3 bg-indigo-50 rounded-lg border border-indigo-200 mb-4" data-testid="bulk-action-bar">
                      <span className="text-sm font-semibold text-indigo-800">{selected.size} selected</span>
                      <button onClick={selectAll} className="text-xs text-indigo-600 hover:underline" data-testid="select-all-btn">Select All</button>
                      <button onClick={deselectAll} className="text-xs text-gray-500 hover:underline" data-testid="deselect-all-btn">Deselect All</button>
                      <div className="flex-1" />
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setShowMoveModal(true)}
                        disabled={selected.size === 0 || bulkLoading}
                        className="border-indigo-300 text-indigo-700 hover:bg-indigo-100"
                        data-testid="bulk-move-btn"
                      >
                        <Move className="h-3.5 w-3.5 mr-1.5" /> Move to Vehicle
                      </Button>
                      <Button
                        size="sm"
                        onClick={handleBulkDelete}
                        disabled={selected.size === 0 || bulkLoading}
                        className="bg-red-600 hover:bg-red-700"
                        data-testid="bulk-delete-btn"
                      >
                        {bulkLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Trash2 className="h-3.5 w-3.5 mr-1.5" />}
                        Delete ({selected.size})
                      </Button>
                    </div>
                  )}

                  {/* Slug Mapping */}
                  <div className="flex items-center gap-3 pt-3 border-t border-gray-100">
                    <label className="text-sm font-medium text-gray-600 whitespace-nowrap">Link to Fleet Page:</label>
                    <select
                      className="flex-1 text-sm border border-gray-300 rounded-md px-3 py-2 bg-white"
                      value={vehicleMedia.vehicle_slug || ''}
                      onChange={async (e) => {
                        const slug = e.target.value;
                        await fetch(`${API}/api/fleet-media/${selectedVehicle.vehicle_id}/slug`, {
                          method: 'PUT', headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ slug })
                        });
                        setVehicleMedia(prev => ({ ...prev, vehicle_slug: slug }));
                        loadAllMedia();
                      }}
                      data-testid="slug-selector"
                    >
                      <option value="">-- Not linked --</option>
                      {FLEET_SLUGS.map(s => <option key={s.slug} value={s.slug}>{s.name}</option>)}
                    </select>
                  </div>

                  {uploading && (
                    <div className="mt-3 flex items-center gap-2 text-sm text-blue-600">
                      <Loader2 className="h-4 w-4 animate-spin" /> <span>{uploadProgress}</span>
                    </div>
                  )}
                </Card>

                {/* Images Grid */}
                <Card className="p-6 border-gray-200">
                  <h4 className="font-bold text-black mb-2 flex items-center gap-2">
                    <Image className="h-5 w-5 text-blue-600" /> Photos ({vehicleMedia.images.length})
                  </h4>
                  {!bulkMode && vehicleMedia.images.length > 1 && (
                    <p className="text-xs text-gray-400 mb-4 flex items-center gap-1">
                      <GripVertical className="h-3 w-3" /> Drag to reorder. First photo = cover image.
                    </p>
                  )}
                  {vehicleMedia.images.length === 0 ? (
                    <div className="text-center py-8 text-gray-400">
                      <Image className="h-10 w-10 mx-auto mb-2 opacity-40" />
                      <p className="text-sm">No photos uploaded yet</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                      {vehicleMedia.images.map((img, idx) => {
                        const isSelected = selected.has(img.file_id);
                        return (
                          <div
                            key={img.file_id}
                            draggable={!bulkMode}
                            onDragStart={() => handleDragStart(idx)}
                            onDragEnter={() => handleDragEnter(idx)}
                            onDragEnd={handleDragEnd}
                            onDragOver={(e) => e.preventDefault()}
                            onClick={bulkMode ? () => toggleSelect(img.file_id) : undefined}
                            className={`relative group rounded-lg overflow-hidden bg-gray-100 aspect-square ${
                              bulkMode ? 'cursor-pointer' : 'cursor-grab active:cursor-grabbing'
                            } ${isSelected ? 'ring-3 ring-indigo-500 ring-offset-2' : ''}`}
                            data-testid={`media-image-${img.file_id}`}
                          >
                            {/* Bulk checkbox */}
                            {bulkMode && (
                              <div className="absolute top-2 left-2 z-20">
                                {isSelected
                                  ? <CheckSquare className="h-6 w-6 text-indigo-500 drop-shadow-md" />
                                  : <Square className="h-6 w-6 text-white drop-shadow-md" />
                                }
                              </div>
                            )}
                            {/* Cover badge */}
                            {idx === 0 && !bulkMode && (
                              <span className="absolute top-1.5 left-1.5 z-10 bg-yellow-400 text-black text-[9px] font-bold px-1.5 py-0.5 rounded" data-testid="cover-badge">
                                COVER
                              </span>
                            )}
                            {!bulkMode && (
                              <div className="absolute top-1.5 right-1.5 z-10 opacity-0 group-hover:opacity-100 transition-opacity">
                                <GripVertical className="h-5 w-5 text-white drop-shadow-md" />
                              </div>
                            )}
                            <img src={`${API}${img.url}`} alt={img.filename} className="w-full h-full object-cover pointer-events-none" />
                            {!bulkMode && (
                              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                                <button onClick={(e) => { e.stopPropagation(); setAsFeatured(img.url); }} className="p-2 bg-white rounded-full text-yellow-500 hover:bg-yellow-50" title="Set as featured">
                                  <Star className="h-4 w-4" />
                                </button>
                                <button onClick={(e) => { e.stopPropagation(); handleDelete('image', img.file_id); }} className="p-2 bg-white rounded-full text-red-500 hover:bg-red-50" title="Delete">
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            )}
                            <p className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-[10px] px-2 py-1 truncate">
                              {img.filename}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </Card>

                {/* Videos Grid */}
                <Card className="p-6 border-gray-200">
                  <h4 className="font-bold text-black mb-4 flex items-center gap-2">
                    <Film className="h-5 w-5 text-purple-600" /> Videos ({vehicleMedia.videos.length})
                  </h4>
                  {vehicleMedia.videos.length === 0 ? (
                    <div className="text-center py-8 text-gray-400">
                      <Film className="h-10 w-10 mx-auto mb-2 opacity-40" />
                      <p className="text-sm">No videos uploaded yet</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {vehicleMedia.videos.map((vid) => {
                        const isSelected = selected.has(vid.file_id);
                        return (
                          <div
                            key={vid.file_id}
                            onClick={bulkMode ? () => toggleSelect(vid.file_id) : undefined}
                            className={`relative group rounded-lg overflow-hidden bg-black ${bulkMode ? 'cursor-pointer' : ''} ${isSelected ? 'ring-3 ring-indigo-500 ring-offset-2' : ''}`}
                            data-testid={`media-video-${vid.file_id}`}
                          >
                            {bulkMode && (
                              <div className="absolute top-2 left-2 z-20">
                                {isSelected ? <CheckSquare className="h-6 w-6 text-indigo-500" /> : <Square className="h-6 w-6 text-white" />}
                              </div>
                            )}
                            <video src={`${API}${vid.url}`} className="w-full aspect-video object-cover" controls={!bulkMode} preload="metadata" />
                            {!bulkMode && (
                              <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button
                                  onClick={() => setSiteFeaturedVideo(vid.url)}
                                  className={`p-1.5 rounded-full text-xs font-bold ${featuredVideo === vid.url ? 'bg-yellow-400 text-black' : 'bg-white/90 text-gray-700 hover:bg-yellow-100'}`}
                                  title="Set as homepage video"
                                >
                                  <Star className="h-3.5 w-3.5" />
                                </button>
                                <button onClick={() => handleDelete('video', vid.file_id)} className="p-1.5 bg-white/90 rounded-full text-red-500 hover:bg-red-100" title="Delete">
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            )}
                            <div className="p-2 bg-gray-900 text-white">
                              <p className="text-xs truncate">{vid.filename}</p>
                              <p className="text-[10px] text-gray-400">
                                {(vid.size / (1024 * 1024)).toFixed(1)} MB
                                {featuredVideo === vid.url && <span className="ml-2 text-yellow-400 font-semibold">Homepage Featured</span>}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </Card>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Move Modal */}
      {showMoveModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setShowMoveModal(false)} data-testid="move-modal">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[70vh] overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="font-bold text-lg">Move {selected.size} item(s) to...</h3>
              <button onClick={() => setShowMoveModal(false)} className="text-gray-400 hover:text-black"><X className="h-5 w-5" /></button>
            </div>
            <div className="overflow-y-auto max-h-[50vh] p-2">
              {vehicles.filter(v => v.vehicle_id !== selectedVehicle?.vehicle_id).map(v => (
                <button
                  key={v.vehicle_id}
                  onClick={() => handleBulkMove(v.vehicle_id)}
                  disabled={bulkLoading}
                  className="w-full text-left px-4 py-3 hover:bg-gray-100 rounded-lg flex items-center justify-between transition-colors"
                  data-testid={`move-to-${v.vehicle_id}`}
                >
                  <div>
                    <p className="font-semibold text-sm">{v.vehicle_name}</p>
                    <p className="text-xs text-gray-500 capitalize">{v.category?.replace('_', ' ')}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {mediaCount(v.vehicle_id) > 0 && (
                      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{mediaCount(v.vehicle_id)}</span>
                    )}
                    <Move className="h-4 w-4 text-gray-400" />
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminFleetMedia;
